import { Dispatch, Action } from 'redux';
import * as React from 'react';
import { connect } from 'react-redux';
import * as uuidv4 from 'uuid/v4';
import { Rectangle, ClipbookInfo } from 'ark-common';
import { Vector2D } from '../../types/common';
import {
  ComposerMode, BuiltinBackground, ComposerEditingMode, ClipbookState, PagePicture,
} from '../types';
import { SimpleResizeSensor } from '../../components/common/SimpleResizeSensor';
import BusyDialog from '../../components/clipbook/BusyDialog';
import StartDialog from '../../components/clipbook/StartDialog';
import NewPageDialog from '../../components/clipbook/NewPageDialog';
import BlankBackground from './BlankBackground';
import TopToolbar, { TopToolbarMode } from '../../components/clipbook/TopToolbar';
import * as ComposerAction from '../actions/ComposerAction';
import {
  ClipboardState,
  ComposerHistoryState,
} from '../reducers/composer';
import createBlobFromCanvas from '../../drawing/util/createBlobFromCanvas';
import PageViewport from './PageViewport';
import PictureCropper from './PictureCropper';
import { PageSlider } from '../../components/page-slider/PageSlider';
import { DrawingToolState, PastingImage } from '../../drawing/reducers/drawingTool';
import { DrawingMode } from '../../drawing/types';
import DrawingToolAction from '../../drawing/actions/DrawingToolAction';
import DrawingToolbar from '../../components/drawing/DrawingToolbar';
import DrawingCanvas from '../../drawing/components/DrawingCanvas';
import { drawingAreaMargin } from '../../drawing/constants';
import {
  DrawingAction, clearEverything, isSingleAddingAction,
} from '../../drawing/actions/DrawingAction';
import HistoryAction from '../../actions/HistoryAction';
import { canUndo, canRedo } from '../../reducers/history';
import { composerPanelID } from '../../reducers/pages';
import { drawingToolbarHeight } from '../../style/values';
import { PictureStorageState } from '../../drawing/reducers/pictureStorage';
import { UIMode } from '../../../../../common/src/ts/types/UIMode';
import { setUIMode } from '../../actions/UIModeAction';
import {
  MultiroleLinkInfo, MultiroleLinkKind,
} from '../../../../../common/src/ts/types/multiroleLink';
import { AppStates } from '../../reducers/app';
import { Publication } from '../../types/epub';
import getStamps from '../../utils/getStamps';
import { setComposerMode } from '../../actions/AppAction';
import {
  minComposerZoomRatio,
  initialComposerZoom,
  initialComposerPan,
} from '../reducers/view';

interface ComposerProps {
  publication: Publication | undefined;
  mode: ComposerMode;
  clipbook: ClipbookState | null;
  clipboard: ClipboardState | null;
  pagePictures: PictureStorageState<PagePicture>;
  clientRect: Rectangle;
  drawingTool: DrawingToolState;
  history: ComposerHistoryState;
  existingClipbooks: ReadonlyArray<ClipbookInfo>;
  onOpenClipbook: (id: string) => void;
  dispatch: Dispatch<Action>;
}

interface ComposerState {
  editingMode: ComposerEditingMode;
}

const TopToolbarOnComposer = connect((state: AppStates) => {
  const { clipbook, view, clientRect } = state.composer;
  if (clipbook === null) {
    throw new Error('Illegal state.');
  }

  const { currentPage, pages } = clipbook;
  return {
    canZoomIn: (currentPage !== null),
    canZoomOut: (currentPage !== null
      && view.zoom > minComposerZoomRatio(clipbook, clientRect)),
    canResetZoom: (currentPage !== null),
    canGoPrev: (currentPage !== null && currentPage > 0),
    canGoNext: (currentPage !== null && currentPage < pages.length - 1),
  };
})(TopToolbar);

const PageViewportOnComposer = connect((state: AppStates) => {
  return {
    clientRect: state.composer.clientRect,
    view: state.composer.view,
  };
})(PageViewport);

const DrawingCanvasOnComposer = connect((state: AppStates) => {
  const { clipbook, view, drawings, drawingPictures, clientRect } = state.composer;
  if (clipbook === null) {
    throw new Error('Illegal state.');
  }

  const { currentPage } = clipbook;
  if (currentPage === null) {
    throw new Error('Illegal state.');
  }

  return {
    data: drawings[clipbook.pages[currentPage].uniqueID],
    pictureStorage: drawingPictures,
    scale: view.zoom,
    translateX: view.pan.x,
    translateY: view.pan.y,
    pageAreaWidth: clientRect.width,
    pageAreaHeight: clientRect.height,
    margin: drawingAreaMargin,
    focus: true,
  };
})(DrawingCanvas);

class Composer extends React.Component<ComposerProps, ComposerState> {
  private addCroppedPictureAsNewPage() {
    const { clipboard } = this.props;
    if (clipboard === null) {
      throw new Error('Illegal state.');
    }

    const { pictureID, croppingRect } = clipboard;
    const onError = () => {
      alert('エラーが発生しました。');
      this.props.dispatch(ComposerAction.discardClipboard());
    };

    let img: HTMLImageElement | null = new Image();

    img.addEventListener('load', () => {
      if (img === null) {
        return;
      }

      const ownerDocument = img.ownerDocument;
      if (ownerDocument === null) {
        return;
      }

      const canvas = ownerDocument.createElement('canvas');
      canvas.width = Math.ceil(croppingRect.width);
      canvas.height = Math.ceil(croppingRect.height);

      const context = canvas.getContext('2d');
      if (context === null) {
        img = null;
        onError();
        return;
      }

      let { x, y } = croppingRect;
      const { width, height } = canvas;
      x = Math.floor(x);
      y = Math.floor(y);
      context.drawImage(img, x, y, width, height, 0, 0, width, height);
      img = null;

      createBlobFromCanvas(canvas).then((blob) => {
        this.props.dispatch(ComposerAction.addNewPageWithPicture(
          uuidv4(), uuidv4(), blob, URL.createObjectURL(blob), width, height,
        ));
      }).catch(onError);
    });

    img.addEventListener('error', onError);

    this.props.dispatch(ComposerAction.setComposerMode(ComposerMode.ImportingPicture));
    img.src = this.props.pagePictures.items[pictureID].image!.url;
  }

  private handleClickOK = () => {
    const { clipbook } = this.props;
    if (clipbook === null) {
      throw new Error('Illegal state.');
    }

    switch (this.props.mode) {
      case ComposerMode.NewPage: {
        const { clipboard } = this.props;
        if (clipboard === null) {
          throw new Error('Illegal state.');
        }

        this.addCroppedPictureAsNewPage();
        break;
      }

      default:
        throw new Error('Illegal state.');
    }
  }

  private handleClickCancel = () => {
    const { clipbook } = this.props;
    if (clipbook === null) {
      throw new Error('Illegal state.');
    }

    switch (this.props.mode) {
      case ComposerMode.NewPage: {
        const { clipboard } = this.props;
        if (clipboard === null) {
          throw new Error('Illegal state.');
        }

        this.props.dispatch(ComposerAction.discardClipboard());
        break;
      }

      default:
        throw new Error('Illegal state.');
    }
  }

  private handleZoomAndPanMode = () => {
    this.setState({ editingMode: ComposerEditingMode.ZoomAndPan });
  }

  private handleDrawingMode = () => {
    this.setState({ editingMode: ComposerEditingMode.Drawing });
  }

  private handleZoomIn = () => {
    this.props.dispatch(ComposerAction.zoomIn());
  }

  private handleZoomOut = () => {
    this.props.dispatch(ComposerAction.zoomOut());
  }

  private handleResetZoom = () => {
    this.props.dispatch(ComposerAction.resetZoom());
  }

  private handleGoPrev = () => {
    const { clipbook } = this.props;
    if (clipbook === null) {
      throw new Error('Illegal state.');
    }

    const { currentPage } = clipbook;
    if (currentPage === null || currentPage <= 0) {
      throw new Error('Illegal state.');
    }

    this.props.dispatch(ComposerAction.moveToPage(currentPage - 1));
  }

  private handleGoNext = () => {
    const { clipbook } = this.props;
    if (clipbook === null) {
      throw new Error('Illegal state.');
    }

    const { currentPage, pages } = clipbook;
    if (currentPage === null || currentPage >= pages.length - 1) {
      throw new Error('Illegal state.');
    }

    this.props.dispatch(ComposerAction.moveToPage(currentPage + 1));
  }

  private handleGoToPage = (page: number) => {
    const { clipbook } = this.props;
    if (clipbook === null) {
      throw new Error('Illegal state.');
    }

    const { pages } = clipbook;
    if (pages.length === 0 && page === 0) {
      // This case is specially allowed for PageSlider's matter.
      return;
    }

    if (!(0 <= page && page < pages.length)) {
      throw new Error('Invalid argument.');
    }

    this.props.dispatch(ComposerAction.moveToPage(page));
  }

  private handleMultiroleLinkClick = (multiroleLink: MultiroleLinkInfo) => {
    switch (multiroleLink.kind) {
      case MultiroleLinkKind.File:
        alert(`TODO:ファイルを開く UIが更新されマイリンクが使えるようになったら対応
              title: ${multiroleLink.title}
              file: ${multiroleLink.file}
              を開きます`);
        break;
      case MultiroleLinkKind.Web:
        window.open(multiroleLink.url, multiroleLink.title);
        break;
      case MultiroleLinkKind.Page:
        // TODO: multiroleLink.pageへの移動 = reference への移動
        break;
    }
  }

  private handleClickBackToViewer = () => {
    this.props.dispatch(setComposerMode(false));
  }

  private handleOpenNewPageDialog = () => {
    this.props.dispatch(ComposerAction.setComposerMode(ComposerMode.NewPage));
  }

  private handleNewClipbook = () => {
    this.props.dispatch(ComposerAction.createNewClipbook(uuidv4()));
  }

  private handleSubmitNewPage = (backgroundID: string) => {
    this.props.dispatch(ComposerAction.addNewPage(uuidv4(), backgroundID, 1000, 800));
  }

  private handleCancelNewPage = () => {
    this.props.dispatch(ComposerAction.setComposerMode(ComposerMode.Editing));
  }

  private handleResize = (clientRect: Rectangle) => {
    this.props.dispatch(ComposerAction.setClientRect(clientRect));
  }

  private handleChangePan = (pan: Vector2D) => {
    this.props.dispatch(ComposerAction.setPan(pan));
  }

  private handleChangeCroppingRect = (rect: Rectangle) => {
    this.props.dispatch(ComposerAction.setCroppingRect(rect));
  }

  private handleDrawingAction = (action: DrawingAction) => {
    this.props.dispatch(action);
    if (isSingleAddingAction(action)) {
      setUIMode(UIMode.Touch, this.props.dispatch);
    }
  }

  private handleSetPastingImagesAction = (pastingImages: ReadonlyArray<PastingImage>) => {
    this.props.dispatch(DrawingToolAction.setPastingImages(pastingImages));
  }

  private handleUndo = () => {
    this.props.dispatch(HistoryAction.undo());
  }

  private handleRedo = () => {
    this.props.dispatch(HistoryAction.redo());
  }

  private handleDrawingToolAction = (action: DrawingToolAction.Action) => {
    this.props.dispatch(action);
  }

  private handleClearDrawing = () => {
    this.props.dispatch(clearEverything());
  }

  private renderCurrentPage() {
    const { clipbook, publication } = this.props;
    if (clipbook === null) {
      return null;
    }

    const { pages, currentPage } = clipbook;
    if (currentPage === null) {
      return null;
    }

    let background: JSX.Element;
    const { backgroundID, width, height } = pages[currentPage];
    switch (backgroundID) {
      case BuiltinBackground.Blank:
        background = <BlankBackground width={width} height={height} />;
        break;

      default: {
        const { pagePictures } = this.props;
        if (!(backgroundID in pagePictures.items)) {
          throw new Error('Illegal state.');
        }

        const item = pagePictures.items[backgroundID];
        if (item.image === null) {
          background = <BlankBackground width={width} height={height} />;
          break;
        }

        const { url } = item.image;
        background = <img key={url} src={url} width={width} height={height} />;
        break;
      }
    }

    return [
      <PageViewportOnComposer
        key="page"
        enableZoomAndPan={this.state.editingMode === ComposerEditingMode.ZoomAndPan}
        contentWidth={width}
        contentHeight={height}
        onChangePan={this.handleChangePan}>
        {background}
      </PageViewportOnComposer>,
      <DrawingCanvasOnComposer
        stamps={getStamps(publication)}
        key="drawing-canvas"
        panelID={composerPanelID}
        toolState={{
          ...this.props.drawingTool,
          mode: this.state.editingMode === ComposerEditingMode.Drawing ?
            this.props.drawingTool.mode : DrawingMode.Disabled,
        }}
        fundamentalWidth={width}
        fundamentalHeight={height}
        onSetPastingImages={this.handleSetPastingImagesAction}
        onMultiroleLinkClick={this.handleMultiroleLinkClick}
        onDrawingAction={this.handleDrawingAction} />,
    ];
  }

  private renderClipboard() {
    const { clipboard } = this.props;
    if (clipboard === null) {
      throw new Error('Illegal state.');
    }

    const viewParams = {
      clientWidth: this.props.clientRect.width,
      clientHeight: this.props.clientRect.height,
      contentWidth: clipboard.pictureWidth,
      contentHeight: clipboard.pictureHeight,
    };

    const scaling = initialComposerZoom(viewParams);
    const translation = initialComposerPan(viewParams, scaling);

    return (
      <PictureCropper scaling={scaling} translation={translation}
        clientWidth={viewParams.clientWidth}
        clientHeight={viewParams.clientHeight}
        pictureURL={this.props.pagePictures.items[clipboard.pictureID].image!.url}
        pictureWidth={clipboard.pictureWidth}
        pictureHeight={clipboard.pictureHeight}
        croppingRect={clipboard.croppingRect}
        onChangeCroppingRect={this.handleChangeCroppingRect} />
    );
  }

  private renderDrawingToolbar() {
    const style: React.CSSProperties = {
      width: '100%',
      display: this.state.editingMode === ComposerEditingMode.Drawing ? 'block' : 'none',
    };

    return (
      <div style={style}>
        <DrawingToolbar
          toolState={this.props.drawingTool}
          canUndo={canUndo(this.props.history)}
          canRedo={canRedo(this.props.history)}
          onUndo={this.handleUndo}
          onRedo={this.handleRedo}
          onToolAction={this.handleDrawingToolAction}
          onClearDrawing={this.handleClearDrawing} />
      </div>
    );
  }

  private renderPageSlider() {
    const { clipbook } = this.props;
    if (clipbook === null) {
      throw new Error('Illegal state.');
    }

    const style: React.CSSProperties = {
      width: '100%',
      display: this.state.editingMode !== ComposerEditingMode.Drawing ? 'block' : 'none',
    };

    return (
      <div style={style}>
        <PageSlider
          currentPage={(clipbook.currentPage === null) ? 0 : clipbook.currentPage}
          numPages={Math.max(1, clipbook.pages.length)}
          onChange={this.handleGoToPage} />
      </div>
    );
  }

  private renderScreen() {
    const { clipbook } = this.props;
    if (clipbook === null) {
      return null;
    }

    const mainStyle = {
      marginTop: '64px',
    };

    let topCaption: string;
    let topToolbarMode: TopToolbarMode;
    if (this.props.clipboard === null) {
      topCaption = clipbook.name;
      topToolbarMode = TopToolbarMode.Editing;
    } else {
      topCaption = '範囲選択';
      topToolbarMode = TopToolbarMode.OKCancel;
    }

    const bottomToolbarStyle: React.CSSProperties = {
      position: 'fixed',
      left: 0,
      bottom: 0,
      right: 0,
      height: `${drawingToolbarHeight}px`,
      display: this.props.clipboard === null ? 'flex' : 'none',
      flex: '1 0 100%',
      alignItems: 'center',
      justifyContent: 'center',
    };

    return [
      <TopToolbarOnComposer key="top-toolbar"
        mode={topToolbarMode}
        editingMode={this.state.editingMode}
        caption={topCaption}
        onClickZoomAndPan={this.handleZoomAndPanMode}
        onClickDrawing={this.handleDrawingMode}
        onClickOK={this.handleClickOK}
        onClickCancel={this.handleClickCancel}
        onClickZoomIn={this.handleZoomIn}
        onClickZoomOut={this.handleZoomOut}
        onClickResetZoom={this.handleResetZoom}
        onClickPrev={this.handleGoPrev}
        onClickNext={this.handleGoNext}
        onClickNewPage={this.handleOpenNewPageDialog}
        onClickBackToViewer={this.handleClickBackToViewer} />,
      <div id="main" key="main" style={mainStyle}>
        <SimpleResizeSensor onResize={this.handleResize} />
        {this.props.clipboard === null ? this.renderCurrentPage() : this.renderClipboard()}
      </div>,
      <div key="bottom-toolbar"
        id="controls"
        style={bottomToolbarStyle}>
        {this.renderDrawingToolbar()}
        {this.renderPageSlider()}
      </div>,
    ];
  }

  private isBusy(): [boolean, string] {
    switch (this.props.mode) {
      case ComposerMode.LoadingIndex:
        return [true, 'バインダー一覧を読み込み中...'];
      case ComposerMode.LoadingClipbook:
        return [true, 'バインダーを読み込み中...'];
      case ComposerMode.ImportingPicture:
        return [true, '処理中...'];
      default:
        return [false, ''];
    }
  }

  public render() {
    const { clipbook } = this.props;
    const [isBusy, busyCaption] = this.isBusy();

    return [
      this.renderScreen(),
      <StartDialog key="start"
        visible={this.props.mode === ComposerMode.Start && this.props.clipbook === null}
        existingClipbooks={this.props.existingClipbooks}
        onNewClipbook={this.handleNewClipbook}
        onOpenClipbook={this.props.onOpenClipbook} />,
      <NewPageDialog key="new-page"
        visible={this.props.mode === ComposerMode.NewPage && this.props.clipboard === null}
        cancelable={clipbook !== null && clipbook.pages.length > 0}
        onSubmitNewPage={this.handleSubmitNewPage}
        onCancelNewPage={this.handleCancelNewPage} />,
      <BusyDialog key="busy" visible={isBusy} caption={busyCaption} />,
    ];
  }

  public constructor(props: Readonly<ComposerProps>) {
    super(props);

    this.state = { editingMode: ComposerEditingMode.ZoomAndPan };
    props.dispatch(ComposerAction.loadClipbookIndex([]));
  }
}

function mapStateToProps(state: AppStates) {
  return {
    publication: state.publication,
    mode: state.composer.mode,
    clipbook: state.composer.clipbook,
    clipboard: state.composer.clipboard,
    pagePictures: state.composer.pagePictures,
    clientRect: state.composer.clientRect,
    drawingTool: state.composer.drawingTool,
    history: state.composer.history,
    existingClipbooks: state.composer.existingClipbooks,
  };
}

export default connect(mapStateToProps)(Composer);
